import { AxiosInstance } from 'axios'
import {
    Folder,
    FolderCreate,
    FolderUpdate,
    ListFolderParams,
    LoadMultiFileParams,
    PaginatedFolderResponse,
    ProjectFolderFlatResponse,
    FolderTreeResponse,
} from './folder.type'

export class FolderApi {
    constructor(private readonly client: AxiosInstance) {}

    /**
     * Get all folders with pagination and filtering
     */
    async list(params?: ListFolderParams): Promise<PaginatedFolderResponse> {
        const response = await this.client.get<PaginatedFolderResponse>('/folders', {
            params,
        })
        return response.data
    }

    /**
     * Create a new folder
     */
    async create(payload: FolderCreate): Promise<Folder> {
        const response = await this.client.post<Folder>('/folders', payload)
        return response.data
    }

    /**
     * Update a folder
     */
    async update(id: string, payload: FolderUpdate): Promise<Folder> {
        const response = await this.client.put<Folder>(`/folders/${id}`, payload)
        return response.data
    }

    /**
     * Delete a folder (soft delete)
     */
    async delete(id: string): Promise<{ partial?: boolean }> {
        const response = await this.client.delete<{ partial?: boolean }>(`/folders/${id}`)
        return response.data
    }

    /**
     * Get folder details by id
     */
    async get(id: string): Promise<Folder> {
        const response = await this.client.get<Folder>(`/folders/${id}`)
        return response.data
    }

    /**
     * Get child folders of a parent folder
     */
    async getChildren(parentId: string, params?: ListFolderParams): Promise<PaginatedFolderResponse> {
        const response = await this.client.get<PaginatedFolderResponse>(`/folders/${parentId}/children`, {
            params,
        })
        return response.data
    }

    /**
     * Get folder hierarchy/path from root to current folder
     */
    async getFolderPath(id: string): Promise<Folder[]> {
        const response = await this.client.get<Folder[]>(`/folders/${id}/path`)
        return response.data
    }

    /**
     * Get all folders by project id
     */
    async getByProjectId(
        projectId: string,
        params?: Omit<ListFolderParams, 'projectId'>,
    ): Promise<PaginatedFolderResponse> {
        const response = await this.client.get<PaginatedFolderResponse>(`/projects/${projectId}/folders`, {
            params,
        })
        return response.data
    }

    async loadMultiFile(loadParams: LoadMultiFileParams): Promise<ProjectFolderFlatResponse> {
        const response = await this.client.get<ProjectFolderFlatResponse>('/folders/lazy-load', {
            params: loadParams,
        })
        return response.data
    }

    /**
     * Bulk delete folders (soft delete)
     */
    async bulkDelete(ids: string[]): Promise<void> {
        await this.client.delete('/folders', { data: { ids } })
    }

    /**
     * Reorder folders
     */
    async reorder(payload: { items: { id: string; sortOrder: number; parentId: string | null }[] }): Promise<void> {
        await this.client.put('/folders/reorder', payload)
    }

    /**
     * Get folder tree information for breadcrumbs
     */
    async getFolderTree(id: string): Promise<FolderTreeResponse> {
        const response = await this.client.get<FolderTreeResponse>(`/folders/${id}/tree-folders`)
        return response.data
    }

    /**
     * Export folders/documents to Excel
     */
    async exportExcel(params: LoadMultiFileParams): Promise<Blob> {
        const response = await this.client.get('/folders/export', {
            params,
            responseType: 'blob',
        })
        return response.data
    }
}
